import { useCallback, useState, useEffect, useRef } from "react";
import { useBrowserWallet } from "@/features/wallet";
import { useAuth } from "./useAuth";
import { authAPI, type VerifyRequest } from "../../../api/auth";
import type { TxSignatureResult } from "@nullaone/wallet-core";
import { useAuthStore } from "@/store/auth-store";
import bs58 from "bs58";

/**
 * Type guard to check if the signature result is a transaction-based signature
 */
function isTxSignatureResult(
  result: Uint8Array | TxSignatureResult,
): result is TxSignatureResult {
  return (
    typeof result === "object" &&
    "serializedTx" in result &&
    "blockhash" in result
  );
}

export type AuthStep =
  | "idle"
  | "loading"
  | "preview"
  | "signing"
  | "success"
  | "error";

export interface UseAuthModalReturn {
  isDialogOpen: boolean;
  openAuth: () => void;
  closeDialog: () => void;
  confirmSign: () => Promise<void>;
  step: AuthStep;
  challengeMessage: string;
  walletAddress: string;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
}

export function useAuthModal(): UseAuthModalReturn {
  const { isAuthenticated, logout } = useAuth();
  const { connected, publicKey, signMessage, openModal, isModalOpen } =
    useBrowserWallet();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [step, setStep] = useState<AuthStep>("idle");
  const [challengeMessage, setChallengeMessage] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [blockhash, setBlockhash] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if we should auto-open signature dialog after connection
  const shouldAutoSignRef = useRef(false);
  const prevConnectedRef = useRef(connected);
  // Track pending closeDialog timeout to prevent race condition
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestChallenge = useCallback(async (address: string) => {
    setIsLoading(true);
    setStep("loading");
    setError(null);
    setWalletAddress(address);
    setBlockhash(undefined);

    try {
      const response = await authAPI.getChallenge(address);
      setChallengeMessage(response.message);
      setBlockhash(response.blockhash); // Store for Ledger fallback
      setStep("preview");
    } catch (err) {
      console.error("[useAuthModal] Failed to get challenge:", err);
      setError(err instanceof Error ? err.message : "Failed to get challenge");
      setStep("error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Watch for wallet connection when we're waiting for it
  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = connected;

    // If we just connected and should auto-sign, proceed with auth.
    // Only consume the flag when we actually open the dialog — otherwise
    // the picker-modal-still-open case would drop the flag before the
    // state-triggered effect below can pick it up after modal close.
    if (
      !wasConnected &&
      connected &&
      publicKey &&
      shouldAutoSignRef.current &&
      !isModalOpen
    ) {
      shouldAutoSignRef.current = false;
      setIsDialogOpen(true);
      requestChallenge(publicKey);
    }
  }, [connected, publicKey, isModalOpen, requestChallenge]);

  // When wallet modal closes after connection, open signature dialog
  useEffect(() => {
    if (shouldAutoSignRef.current && connected && publicKey && !isModalOpen) {
      shouldAutoSignRef.current = false;
      requestAnimationFrame(() => {
        setIsDialogOpen(true);
        requestChallenge(publicKey);
      });
    }
  }, [connected, publicKey, isModalOpen, requestChallenge]);

  // Auto-open signature dialog is handled by the connected/isModalOpen watchers above

  const openAuth = useCallback(() => {
    // Cancel any pending closeDialog timeout to prevent race condition
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    // Reset to known state
    setStep("idle");
    setError(null);

    if (!connected || !publicKey) {
      // Not connected - set flag and open browser wallet modal
      shouldAutoSignRef.current = true;
      openModal(); // Always open "select" step - shows all stored wallets
    } else {
      // Already connected - open signature dialog directly
      setIsDialogOpen(true);
      requestChallenge(publicKey);
    }
  }, [connected, publicKey, openModal, requestChallenge]);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    shouldAutoSignRef.current = false;

    // Clear any existing timeout first
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setStep("idle");
      setChallengeMessage("");
      setWalletAddress("");
      setError(null);
      closeTimeoutRef.current = null;
    }, 200);
  }, []);

  const confirmSign = useCallback(async () => {
    if (!signMessage || !publicKey) {
      setError("Wallet not connected");
      setStep("error");
      return;
    }

    setIsLoading(true);
    setStep("signing");
    setError(null);

    try {
      const messageBytes = new TextEncoder().encode(challengeMessage);
      // Pass blockhash for Ledger System Transfer fallback
      const signResult = await signMessage(messageBytes, blockhash);

      // Build verify request based on signature type
      let verifyRequest: VerifyRequest;

      if (isTxSignatureResult(signResult)) {
        // Transaction-based signature (Ledger fallback)
        verifyRequest = {
          wallet: walletAddress,
          message: challengeMessage,
          signedTransaction: signResult.serializedTx,
        };
      } else {
        // Standard off-chain signature
        const signatureBase58 = bs58.encode(signResult);
        verifyRequest = {
          wallet: walletAddress,
          message: challengeMessage,
          signature: signatureBase58,
        };
      }

      const verifyResponse = await authAPI.verify(verifyRequest);

      const expiresIn = parseInt(verifyResponse.expiresIn.replace("s", ""), 10);

      const { setAuthenticated } = useAuthStore.getState();
      setAuthenticated(
        verifyResponse.wallet,
        verifyResponse.token,
        verifyResponse.jti,
        verifyResponse.sid,
        expiresIn,
      );

      setStep("success");
      setIsLoading(false);

      setTimeout(() => closeDialog(), 1000);
    } catch (err) {
      console.error("[useAuthModal] Signing failed:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Authentication failed";

      if (
        errorMessage.includes("rejected") ||
        errorMessage.includes("cancelled")
      ) {
        setError("Signature cancelled");
      } else {
        setError(errorMessage);
      }
      setStep("error");
      setIsLoading(false);
    }
  }, [
    signMessage,
    publicKey,
    challengeMessage,
    walletAddress,
    blockhash,
    closeDialog,
  ]);

  const retry = useCallback(() => {
    if (publicKey) {
      setStep("idle");
      requestChallenge(publicKey);
    } else {
      openAuth();
    }
  }, [publicKey, requestChallenge, openAuth]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return {
    isDialogOpen,
    openAuth,
    closeDialog,
    confirmSign,
    step,
    challengeMessage,
    walletAddress,
    isLoading,
    error,
    retry,
    isAuthenticated,
    logout,
  };
}
